import React, { type ComponentProps, useCallback } from "react";

import { Flex } from "../../../../mads";
// Note: This file is a copy of the ChipFormField component from the IFL library.
import { Chip } from "./Chip";
import { FormField } from "./FormField";

type ChipSize = "sm" | "md" | "lg";
type ChipFormFieldItem = Pick<ComponentProps<typeof Chip>, "label" | "value">;

export interface ChipFormFieldProps {
  /**
   * An array of props/attributes for the Chip components to render in this form field.
   */
  items: ChipFormFieldItem[];
  /**
   * the label to display on the form field
   */
  label: string;
  /**
   * whether the form data for this field is invalid
   */
  invalid?: boolean;
  /**
   * whether this field is disabled
   */
  disabled?: boolean;
  /**
   * optional helper text for the label component
   */
  helperText?: string;
  /**
   * optional error text for the label component
   */
  errorText?: string;
  /**
   * optional showRequiredIndicator for the label component
   */
  showRequiredIndicator?: boolean;
  /**
   * Size of the Chip. Use the values `sm`, `md`, or `lg`.
   */
  size?: ChipSize;
  /**
   * The value of the currently checked chip option, leave as undefined to represent none selected
   */
  checkedValue: string | undefined;
  /**
   * Callback for when the checked chip changes, does not fire if the selected chip is pressed again
   *   - passes the selected chip value as a parameter
   */
  onChange: (value: string) => void;
}

/**
 * React Native implementation of ChipFormField
 *
 * N.B. we only implement the 'single' type here.
 *
 * @see https://experience.indeed.design/components/ids/chip-form-field
 */
export function ChipFormField({
  items,
  label,
  invalid,
  disabled,
  helperText,
  errorText,
  size,
  showRequiredIndicator,
  checkedValue,
  onChange,
}: ChipFormFieldProps) {
  const onSelect = useCallback(
    (value: string) => {
      if (checkedValue === value) {
        return;
      }
      onChange(value);
    },
    [checkedValue, onChange],
  );

  return (
    <FormField
      label={label}
      helperText={helperText}
      errorText={errorText}
      invalid={invalid}
      disabled={disabled}
      showRequiredIndicator={showRequiredIndicator}
    >
      <Flex sx={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
        {items.map(({ label, value }, index) => (
          <Chip
            key={index}
            size={size}
            invalid={invalid}
            disabled={disabled}
            label={label}
            checked={value === checkedValue}
            onSelect={onSelect}
            value={value}
          />
        ))}
      </Flex>
    </FormField>
  );
}

ChipFormField.displayName = "ChipFormField";
